import { useAuth } from '@clerk/clerk-react';
import { useCallback, useMemo } from 'react';

// API base URL:
// - In production: empty string (same origin, nginx proxies /api to backend)
// - In development: Vite proxy handles /api routes (see vite.config.ts)
// Never hardcode localhost - it breaks production deployments
const API_BASE = '';

/**
 * Custom hook for making authenticated API requests
 * Functions are memoized to maintain stable references across renders
 */
export function useApi() {
  const { getToken, isSignedIn } = useAuth();

  const fetchApi = useCallback(async <T = unknown>(
    path: string,
    options?: RequestInit
  ): Promise<T> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    // Add auth token if signed in
    if (isSignedIn) {
      const token = await getToken();
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${API_BASE}/api${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP ${response.status}`);
    }

    return response.json();
  }, [getToken, isSignedIn]);

  // Memoize the returned object to prevent unnecessary re-renders
  return useMemo(() => ({
    fetchApi,
    get: <T = unknown>(path: string) => fetchApi<T>(path, { method: 'GET' }),
    post: <T = unknown>(path: string, data?: unknown) =>
      fetchApi<T>(path, { method: 'POST', body: JSON.stringify(data) }),
    patch: <T = unknown>(path: string, data?: unknown) =>
      fetchApi<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: <T = unknown>(path: string) => fetchApi<T>(path, { method: 'DELETE' }),
  }), [fetchApi]);
}

/**
 * Non-hook version for use outside React components
 */
export async function apiRequest<T = any>(
  path: string,
  options?: RequestInit & { token?: string }
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  if (options?.token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${options.token}`;
  }

  const response = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}
